import React from 'react';
import { Table, Tag } from 'antd';
import { useTranslation } from '../../../../utils/useI18n';

const COLORS = { primary: '#00d4ff' };
const fmtTime = t => t ? new Date(t).toLocaleString() : '-';

export default function CellTable({ data }) {
  const { t } = useTranslation();

  const columns = [
    { title: 'sn', dataIndex: 'sn', key: 'sn', render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span>, width: 130 },
    { title: t('cloud.deviceNo'), dataIndex: 'global_no', key: 'global_no', render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span>, width: 110 },
    { title: t('cloud.name'), dataIndex: 'name', key: 'name', render: (n, r) => n || r.global_no || '-', width: 130 },
    { title: t('cloud.devNo'), dataIndex: 'dev_no', key: 'dev_no', render: v => v || '-', width: 80 },
    { title: '电压数量', dataIndex: 'voltage_count', key: 'voltage_count', render: v => v ?? '-', width: 90 },
    { title: '无效电压', dataIndex: 'invalid_voltage_count', key: 'invalid_voltage_count', render: v => v ?? '-', width: 90 },
    { title: '温度数量', dataIndex: 'temperature_count', key: 'temperature_count', render: v => v ?? '-', width: 90 },
    { title: '无效温度', dataIndex: 'invalid_temperature_count', key: 'invalid_temperature_count', render: v => v ?? '-', width: 90 },
    { title: t('cloud.recordTime'), dataIndex: 'recorded_at', key: 'recorded_at', render: fmtTime, width: 160 },
  ];

  const expandedRowRender = (record) => {
    const voltages = Array.isArray(record?.voltages_v) ? record.voltages_v : [];
    const temperatures = Array.isArray(record?.temperatures_c) ? record.temperatures_c : [];
    const validVoltages = voltages.filter(v => v != null);
    const maxVoltage = validVoltages.length > 0 ? Math.max(...validVoltages) : null;
    const minVoltage = validVoltages.length > 0 ? Math.min(...validVoltages) : null;
    const validTemps = temperatures.filter(t => t != null);
    const maxTemp = validTemps.length > 0 ? Math.max(...validTemps) : null;
    const minTemp = validTemps.length > 0 ? Math.min(...validTemps) : null;
    const eps = 0.0001;

    const getVoltageTag = (v, i) => {
      if (v == null) return <span key={i} style={{ display: 'inline-block', minWidth: 80, textAlign: 'center', padding: '2px 4px', color: '#888', fontSize: 12 }}>#{(record.voltage_start_index ?? 0) + i}: 无效</span>;
      const isMax = validVoltages.length > 1 && Math.abs(v - maxVoltage) < eps;
      const isMin = validVoltages.length > 1 && Math.abs(v - minVoltage) < eps;
      const color = isMax ? '#52c41a' : isMin ? '#ff4d4f' : '#ffffff';
      const fontWeight = (isMax || isMin) ? 'bold' : 'normal';
      const label = isMax ? ' ↑最高' : isMin ? ' ↓最低' : '';
      return <span key={i} style={{ display: 'inline-block', minWidth: 80, textAlign: 'center', padding: '2px 4px', color, fontWeight, fontSize: 12 }}>#{(record.voltage_start_index ?? 0) + i}: {v.toFixed(3)}{label}</span>;
    };

    const getTempTag = (t, i) => {
      if (t == null) return <span key={i} style={{ display: 'inline-block', minWidth: 80, textAlign: 'center', padding: '2px 4px', color: '#888', fontSize: 12 }}>#{(record.temperature_start_index ?? 0) + i}: 无效</span>;
      const isMax = validTemps.length > 1 && Math.abs(t - maxTemp) < eps;
      const isMin = validTemps.length > 1 && Math.abs(t - minTemp) < eps;
      const color = isMax ? '#52c41a' : isMin ? '#ff4d4f' : '#ffffff';
      const fontWeight = (isMax || isMin) ? 'bold' : 'normal';
      const label = isMax ? ' ↑最高' : isMin ? ' ↓最低' : '';
      return <span key={i} style={{ display: 'inline-block', minWidth: 80, textAlign: 'center', padding: '2px 4px', color, fontWeight, fontSize: 12 }}>#{(record.temperature_start_index ?? 0) + i}: {t.toFixed(1)}{label}</span>;
    };

    return (
      <div style={{ padding: '8px 0' }}>
        {voltages.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 6 }}>
              电芯电压 (V) - 从{record.voltage_start_index}开始 &nbsp;
              <Tag style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff', fontWeight: 'bold' }}>最高#{voltages.findIndex(v => v != null && Math.abs(v - maxVoltage) < eps) + (record.voltage_start_index ?? 0)}: {maxVoltage?.toFixed(3) ?? '-'}</Tag>
              <Tag style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff', fontWeight: 'bold' }}>最低#{voltages.findIndex(v => v != null && Math.abs(v - minVoltage) < eps) + (record.voltage_start_index ?? 0)}: {minVoltage?.toFixed(3) ?? '-'}</Tag>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {voltages.map((v, i) => getVoltageTag(v, i))}
            </div>
          </div>
        )}
        {temperatures.length > 0 && (
          <div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 6 }}>
              温度采样点 (℃) - 从{record.temperature_start_index}开始 &nbsp;
              <Tag style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff', fontWeight: 'bold' }}>最高#{temperatures.findIndex(t => t != null && Math.abs(t - maxTemp) < eps) + (record.temperature_start_index ?? 0)}: {maxTemp?.toFixed(1) ?? '-'}</Tag>
              <Tag style={{ backgroundColor: '#1677ff', borderColor: '#1677ff', color: '#fff', fontWeight: 'bold' }}>最低#{temperatures.findIndex(t => t != null && Math.abs(t - minTemp) < eps) + (record.temperature_start_index ?? 0)}: {minTemp?.toFixed(1) ?? '-'}</Tag>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {temperatures.map((t, i) => getTempTag(t, i))}
            </div>
          </div>
        )}
        {voltages.length === 0 && temperatures.length === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.6)' }}>无单体明细数据</div>
        )}
      </div>
    );
  };

  if (!data || data.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: COLORS.primary, fontSize: 14, fontWeight: 600, marginBottom: 8, borderLeft: '3px solid #f4c542', paddingLeft: 8 }}>
        BMS单体电压/温度 {data.length} {t('cloud.telemetryRecords')}
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 5, showSizeChanger: false }}
        style={{ background: 'transparent' }}
        scroll={{ x: 1400 }}
        tableLayout="fixed"
        expandable={{ expandedRowRender, defaultExpandedRowKeys: [] }}
      />
    </div>
  );
}
